/**
 * Asking a member's phone whether it is still there.
 *
 * ## Why asking is necessary
 *
 * The phone badge is derived from heartbeat freshness: the phone posts every
 * sixty seconds and the web treats the last one as good for five minutes. Kill
 * the browser — swipe the app away, which is how a member finishes with their
 * phone — and the badge keeps showing whatever it last said for up to five
 * minutes. Reported exactly that way: "if I kill the browser... it went to
 * distracted and then green when it should show not connected".
 *
 * There IS an unload beacon that posts `inactive`, and it is best-effort by
 * nature: iOS routinely kills a tab without running `pagehide` handlers at all.
 * A signal that only works when the device shuts down politely cannot be the
 * one the badge trusts.
 *
 * ## Why a question rather than presence
 *
 * The realtime server knows which sockets are open, which is tempting and
 * currently unusable: a `Connection` carries a user and a topic set and no
 * device identity, so the server cannot tell a phone's socket from a laptop's.
 * Asking needs no protocol change, works over the HTTP poll as well as the
 * socket — both deliver the device topic — and gives a definite answer rather
 * than an inference.
 *
 * ## What an answer is worth
 *
 * A reply proves the phone is there. Silence proves nothing on its own, which
 * is why silence may only ever TIGHTEN the badge: it can turn a green badge
 * grey, never the reverse, and only while the asking client's own transport is
 * healthy. A member whose desktop cannot reach the server must not be told
 * their phone has gone.
 */

/** Every message in this exchange. */
export const DEVICE_PRESENCE_TYPE = "presence" as const;

/** The only device kind worth asking about today. */
export type PresenceDeviceKind = "phone";

export interface DevicePresencePing {
  type: typeof DEVICE_PRESENCE_TYPE;
  action: "ping";
  /** Correlates a reply with the question that caused it. */
  pingId: string;
  /** Which device is being asked; others ignore it. */
  want: PresenceDeviceKind;
}

export interface DevicePresencePong {
  type: typeof DEVICE_PRESENCE_TYPE;
  action: "pong";
  pingId: string;
  device: PresenceDeviceKind;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return Boolean(x) && typeof x === "object" && !Array.isArray(x);
}

export function parseDevicePresencePing(
  payload: unknown
): DevicePresencePing | null {
  if (!isRecord(payload)) return null;
  if (payload.type !== DEVICE_PRESENCE_TYPE) return null;
  if (payload.action !== "ping") return null;
  if (typeof payload.pingId !== "string" || payload.pingId.length === 0) {
    return null;
  }
  if (payload.want !== "phone") return null;
  return {
    type: DEVICE_PRESENCE_TYPE,
    action: "ping",
    pingId: payload.pingId,
    want: "phone",
  };
}

export function parseDevicePresencePong(
  payload: unknown
): DevicePresencePong | null {
  if (!isRecord(payload)) return null;
  if (payload.type !== DEVICE_PRESENCE_TYPE) return null;
  if (payload.action !== "pong") return null;
  if (typeof payload.pingId !== "string" || payload.pingId.length === 0) {
    return null;
  }
  if (payload.device !== "phone") return null;
  return {
    type: DEVICE_PRESENCE_TYPE,
    action: "pong",
    pingId: payload.pingId,
    device: "phone",
  };
}

/** What the exchange currently says about the phone. */
export type PhonePresence = "present" | "absent" | "unknown";

export interface PhonePresenceInput {
  /**
   * When asking began, or last resumed.
   *
   * Silence is measured from the last sign of life, and before any answer has
   * ever arrived that is the moment the questions started.
   */
  probingSinceMs: number | null;
  /** When the most recent question was asked. */
  lastPingAtMs: number | null;
  /** When an answer last arrived. */
  lastPongAtMs: number | null;
  nowMs: number;
  /** How long silence has to last before it counts. */
  timeoutMs: number;
}

/**
 * Reads the exchange.
 *
 * Silence is measured from the last ANSWER, not the last question. Measuring
 * from the question cannot work: a new one goes out every ten seconds, so
 * `now - lastPingAtMs` never exceeds ten seconds and no timeout longer than the
 * interval can ever elapse. That bug shipped in the first version of this
 * function and hid behind an end-to-end test that was passing for an unrelated
 * reason.
 *
 * `unknown` is a real answer and the most important one: nothing has been asked
 * yet, or not enough silence has accumulated to mean anything. Treating either
 * as absence would flicker the badge grey between questions.
 */
export function phonePresenceVerdict({
  probingSinceMs,
  lastPingAtMs,
  lastPongAtMs,
  nowMs,
  timeoutMs,
}: PhonePresenceInput): PhonePresence {
  if (lastPingAtMs === null || probingSinceMs === null) return "unknown";

  // Answered the latest question, so it is here.
  if (lastPongAtMs !== null && lastPongAtMs >= lastPingAtMs) return "present";

  const lastSignOfLife = lastPongAtMs ?? probingSinceMs;
  return nowMs - lastSignOfLife >= timeoutMs ? "absent" : "unknown";
}
